// src/pages/Settings.js
import { useState, useEffect } from 'react';
import { useAuth } from '../lib/AuthContext';
import { updateBusiness } from '../lib/airtable';
import Nav from '../components/Nav';
import Sidebar from '../components/Sidebar';

export default function Settings() {
  const { business, refreshBusiness } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (business) {
      setForm({
        'AI Agent Name':      business['AI Agent Name']      || 'Sophie',
        'Transfer Number':    business['Transfer Number']    || '',
        'Weekday Hours':      business['Weekday Hours']      || '',
        'Weekend Hours':      business['Weekend Hours']      || '',
        'Service Zip Codes':  business['Service Zip Codes']  || '',
        'Services Offered':   business['Services Offered']   || '',
        'Pricing Table':      business['Pricing Table']      || '',
        'Special Instructions': business['Special Instructions'] || '',
      });
    }
  }, [business]);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  async function handleSave(e) {
    e.preventDefault();
    setError('');
    setSaving(true);
    setSaved(false);
    try {
      await updateBusiness(business.id, form);
      await refreshBusiness();
      setSaved(true);
      setTimeout(() => setSaved(false), 4000);
    } catch (err) {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (!form) return (
    <div className="page"><Nav />
      <div className="app-layout"><Sidebar />
        <main className="main-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="spinner spinner-dark" />
        </main>
      </div>
    </div>
  );

  return (
    <div className="page">
      <Nav />
      <div className="app-layout">
        <Sidebar />
        <main className="main-content">
          <div style={{ maxWidth: 640 }}>

            <div className="fade-up" style={{ marginBottom: 28 }}>
              <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 26, fontWeight: 800, marginBottom: 4 }}>Business Settings</h1>
              <p style={{ color: 'var(--muted)', fontSize: 15 }}>
                Changes save to your AI agent within 15 minutes automatically.
              </p>
            </div>

            {saved && <div className="alert alert-success fade-up">✅ Settings saved! Sophie will update within 15 minutes.</div>}
            {error && <div className="alert alert-error">{error}</div>}

            <form onSubmit={handleSave} className="fade-up-2">

              {/* AI Agent */}
              <div className="card" style={{ marginBottom: 20 }}>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, marginBottom: 20 }}>🤖 AI Receptionist</h2>
                <div className="form-group">
                  <label className="form-label">Receptionist name</label>
                  <input className="form-input" value={form['AI Agent Name']} onChange={set('AI Agent Name')} placeholder="Sophie" />
                  <span className="form-hint">The name your AI uses when answering calls.</span>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Transfer phone number</label>
                  <input className="form-input" type="tel" value={form['Transfer Number']} onChange={set('Transfer Number')} placeholder="(555) 123-4567" />
                  <span className="form-hint">Sophie transfers callers to this number when they need a human.</span>
                </div>
              </div>

              {/* Hours */}
              <div className="card" style={{ marginBottom: 20 }}>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, marginBottom: 20 }}>🕐 Business Hours</h2>
                <div className="form-group">
                  <label className="form-label">Weekday hours</label>
                  <input className="form-input" value={form['Weekday Hours']} onChange={set('Weekday Hours')} placeholder="8:00 AM – 6:00 PM" />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Weekend hours</label>
                  <input className="form-input" value={form['Weekend Hours']} onChange={set('Weekend Hours')} placeholder="9:00 AM – 2:00 PM, or Closed" />
                </div>
              </div>

              {/* Coverage */}
              <div className="card" style={{ marginBottom: 20 }}>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, marginBottom: 20 }}>📍 Service Area</h2>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Service zip codes <span>(one per line)</span></label>
                  <textarea className="form-textarea" value={form['Service Zip Codes']} onChange={set('Service Zip Codes')} placeholder={"77001\n77002\n77003"} style={{ minHeight: 100 }} />
                  <span className="form-hint">Sophie will confirm if a caller is in your service area.</span>
                </div>
              </div>

              {/* Services & Pricing */}
              <div className="card" style={{ marginBottom: 20 }}>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, marginBottom: 20 }}>🛠 Services & Pricing</h2>
                <div className="form-group">
                  <label className="form-label">Services offered <span>(one per line)</span></label>
                  <textarea className="form-textarea" value={form['Services Offered']} onChange={set('Services Offered')} placeholder={"Standard Cleaning\nDeep Cleaning\nMove-Out Cleaning"} style={{ minHeight: 120 }} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Pricing <span>(optional — one per line)</span></label>
                  <textarea className="form-textarea" value={form['Pricing Table']} onChange={set('Pricing Table')} placeholder={"Standard Cleaning: $120\nDeep Cleaning: $200"} style={{ minHeight: 100 }} />
                </div>
              </div>

              {/* Special Instructions */}
              <div className="card" style={{ marginBottom: 28 }}>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700, marginBottom: 20 }}>📝 Special Instructions</h2>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Instructions for Sophie <span>(optional)</span></label>
                  <textarea className="form-textarea" value={form['Special Instructions']} onChange={set('Special Instructions')} placeholder="e.g. Always confirm zip code before booking. We are fully booked on Tuesdays." style={{ minHeight: 100 }} />
                  <span className="form-hint">Any specific rules or information Sophie should know about your business.</span>
                </div>
              </div>

              {/* Locked fields notice */}
              <div className="alert alert-info" style={{ marginBottom: 28, fontSize: 13 }}>
                🔒 <strong>Business name and AI agent ID</strong> are managed by your RingXai admin. Contact support to update these.
              </div>

              <button type="submit" className="btn btn-primary btn-lg" disabled={saving}>
                {saving ? <><span className="spinner" /> Saving...</> : 'Save settings →'}
              </button>

            </form>
          </div>
        </main>
      </div>
    </div>
  );
}
